import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import IconComponent from '../../../IconComponent/IconComponent';
import { MessageInterface } from '../../../../shared/type/message.type';
import useAuth from '../../../../hooks/useAuth';
import { useSafeTranslation } from '../../../../hooks/useSafeTranslation';

interface Props {
  messages: MessageInterface[];
  tenantId: string;
  onDelete: (threadId: number) => void;
  hideDeleteButton?: boolean;
}

export const ParentMessageList = ({ messages, tenantId, onDelete, hideDeleteButton }: Props) => {
  const { t } = useSafeTranslation();
  const { auth } = useAuth();
  const location = useLocation();
  const isAnnouncementBox = location.pathname.includes('announcement');

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    return now.getTime() - date.getTime() < 24 * 60 * 60 * 1000
      ? date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })
      : `${date.toLocaleDateString('pl-PL')} ${date.toLocaleTimeString('pl-PL', {
          hour: '2-digit',
          minute: '2-digit'
        })}`;
  };

  if (messages.length === 0) {
    return (
      <p className="text-center text-gray-500 mt-4">
        {t('parent_mailbox.parent_message.no_message')}
      </p>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="flex pt-spacing-lg px-spacing-xl md:px-spacing-4xl pb-spacing-lg">
        <div className="basis-11/12 flex items-center gap-spacing-lg">
          <p className="basis-5/12 md:basis-3/12 text-left text-xs md:text-sm font-semibold text-grayLight-700 text-nowrap">
            {t('parent_mailbox.parent_message.from')}
          </p>
          <p className="basis-6/12 md:basis-9/12 text-left text-xs md:text-sm font-semibold text-grayLight-700">
            {t('parent_mailbox.parent_message.topic')}
          </p>
        </div>
        <div className="basis-1/12 text-xs md:text-sm font-semibold text-grayLight-700 flex items-center justify-center">
          {t('parent_mailbox.parent_message.date')}
        </div>
      </div>

      {messages
        .sort(
          (a, b) =>
            new Date(b.lastUpdate || b.sent).getTime() - new Date(a.lastUpdate || b.sent).getTime()
        )
        .map((message) => (
          <div
            key={message.id}
            className={`flex pt-spacing-lg px-spacing-xl md:px-spacing-4xl pb-spacing-lg hover:bg-brand-100 ${
              (!message.hasBeanRead || !message.read) && 'bg-grayLight-100'
            }`}>
            <Link
              to={`${isAnnouncementBox ? '' : 'message/'}${message.id}?title=${encodeURIComponent(message.topic)}`}
              className="basis-11/12 flex items-center gap-spacing-lg">
              <p
                className={`basis-5/12 md:basis-3/12 text-left text-xs md:text-sm font-${
                  message.hasBeanRead || message.read ? 'normal' : 'semibold'
                } text-grayLight-700`}>
                {auth.availableTenants.find((t) => Number(t.id) === Number(message.tenantId))?.name}
              </p>
              <p
                className={`b-6/12 md:basis-9/12 text-left text-xs md:text-sm font-${
                  message.hasBeanRead || message.read ? 'normal' : 'semibold'
                } text-grayLight-700`}>
                {message.topic}
              </p>
            </Link>
            {!hideDeleteButton && (
              <div className="basis-1/12 flex items-center justify-center group">
                <p
                  className={`group-hover:hidden text-xs md:text-sm font-${message.hasBeanRead || message.read ? 'normal' : 'semibold'}`}>
                  {formatDate(message.lastUpdate || message.sent)}
                </p>
                <button onClick={() => onDelete(message.id)} title="Usuń wiadomość">
                  <IconComponent
                    color="#171412"
                    iconName="trash"
                    className="hidden group-hover:block"
                  />
                </button>
              </div>
            )}
          </div>
        ))}
    </div>
  );
};
